import { Link } from "react-router-dom"
import { Clock, Calendar, Eye } from "lucide-react"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Badge } from "@/components/ui/badge"
import { formatDate, getInitials, formatNumber } from "@/lib/utils"

// Helper to extract date from Firestore Timestamp or ISO string
function getDate(value: unknown): Date {
  if (!value) return new Date()
  if (typeof value === "string") return new Date(value)
  if (value instanceof Date) return value
  if (typeof value === "object" && "toDate" in value && typeof (value as { toDate: () => Date }).toDate === "function") {
    return (value as { toDate: () => Date }).toDate()
  }
  return new Date()
}

interface PostMetaProps {
  post: {
    readingTime?: number
    viewCount?: number
    publishedAt?: unknown
    createdAt?: unknown
    tags?: string[]
    author?: {
      username?: string
      displayName?: string
      avatarUrl?: string
    }
  }
  showViews?: boolean
}

export function PostMeta({ post, showViews = false }: PostMetaProps) {
  return (
    <div className="flex flex-col space-y-4">
      {/* Author info */}
      <div className="flex items-center space-x-4">
        <Link to={`/user/${post.author?.username}`}>
          <Avatar className="h-12 w-12">
            <AvatarImage src={post.author?.avatarUrl} alt={post.author?.displayName} />
            <AvatarFallback>{getInitials(post.author?.displayName || "U")}</AvatarFallback>
          </Avatar>
        </Link>
        <div>
          <Link
            to={`/user/${post.author?.username}`}
            className="font-medium text-gray-900 hover:text-indigo-600 dark:text-white dark:hover:text-indigo-400"
          >
            {post.author?.displayName}
          </Link>
          <div className="flex items-center text-sm text-gray-500 dark:text-gray-400">
            <Calendar className="mr-1 h-4 w-4" />
            <span>{formatDate(getDate(post.publishedAt || post.createdAt))}</span>
            <span className="mx-2">·</span>
            <Clock className="mr-1 h-4 w-4" />
            <span>{post.readingTime} min read</span>
            {showViews && (
              <>
                <span className="mx-2">·</span>
                <Eye className="mr-1 h-4 w-4" />
                <span>{formatNumber(post.viewCount || 0)} views</span>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Tags */}
      {post.tags && post.tags.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {post.tags.map(tag => (
            <Link key={tag} to={`/tag/${tag}`}>
              <Badge variant="secondary" className="hover:bg-gray-200 dark:hover:bg-gray-700">
                #{tag}
              </Badge>
            </Link>
          ))}
        </div>
      )}
    </div>
  )
}
